import React from 'react';

import classNames from 'classnames';

import BudgetOverviewInfoTip from '../budget_overview/BudgetOverviewInfoTip';
import { FxRate, sectionIncomplete, withIncomplete } from '../ShowInSelect';

import { formatCell, isEmptyCell } from './summaryCellFormat';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

/** The key of a project row's drilldown, shared with the export. */
export const periodRowKey = (sectionKey, row) => `${sectionKey}:${row.id}`;

// Column-generic By period table (IB-642): renders the period `columns` (M/Q/Y)
// left→right with a Total column on the right, Revenue/Expenditure sections
// with a row per selected project, and a semantic-coloured Net section. Totals
// are shown whatever the currencies, labelled when they are mixed.
export default function FinancialTable({ data, columns, mixedCurrencies = false, fx = null, expanded, onToggle }) {
    if (!columns || columns.length === 0) {
        return null;
    }

    // In a chosen currency the totals name it and are formatted in it.
    const label = (text) => {
        if (fx) {
            return $t('{{label}} ({{currency}})', { label: text, currency: fx.show_in });
        }

        return mixedCurrencies ? $t('{{label}} (mixed currencies)', { label: text }) : text;
    };
    const totalLocale = fx?.locale || null;

    return (
        <div className="inbud-table_wrapper inbud-table_sticky-box">
            <table className="inbud-table tw-table-fixed inbud-table_first-sticky">
                <thead>
                    <tr>
                        <th className="tw-w-56 tw-z-10 tw-border-r"></th>
                        {columns.map((column) => (
                            <th key={column.key} className="tw-w-32 tw-border-r">
                                {column.label}
                            </th>
                        ))}
                        <th className="tw-w-32">
                            <span className="tw-inline-flex tw-items-center tw-gap-1">
                                {$t('Total')}
                                <BudgetOverviewInfoTip label={$t('Total')} text={$t('Sum of the periods shown.')} />
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <Section
                        label={$t('Revenue')}
                        sectionKey="revenues"
                        totalLabel={label($t('Total revenue'))}
                        section={data.revenues}
                        columns={columns}
                        expanded={expanded}
                        onToggle={onToggle}
                        totalLocale={totalLocale}
                    />
                    <Section
                        label={$t('Expenditure')}
                        sectionKey="expenditures"
                        totalLabel={label($t('Total expenditure'))}
                        section={data.expenditures}
                        columns={columns}
                        absolute
                        expanded={expanded}
                        onToggle={onToggle}
                        totalLocale={totalLocale}
                    />
                    {/* Net rows carry no single category structure, so the net section has no drilldown. */}
                    <Section label={$t('Net')} sectionKey="net" section={data.total} columns={columns} semantic drilldown={false} />
                </tbody>
                {/* Bottom-line Net Total pinned to the bottom of the scroll box (IB-697). */}
                <tfoot>
                    <TotalRow
                        name={withIncomplete(label($t('Net total')), sectionIncomplete(data.total.children))}
                        row={data.total}
                        columns={columns}
                        locale={totalLocale}
                        semantic
                        footer
                    />
                </tfoot>
            </table>
        </div>
    );
}

function Section({
    label,
    sectionKey,
    totalLabel = null,
    section,
    columns,
    absolute = false,
    semantic = false,
    drilldown = true,
    expanded,
    onToggle,
    totalLocale = null,
}) {
    return (
        <React.Fragment>
            <tr>
                <td className="tw-font-semibold !tw-border-r-0">{label}</td>
                <td colSpan={columns.length + 1} />
            </tr>
            {(section.children || []).map((row) => {
                const key = periodRowKey(sectionKey, row);

                return (
                    <ProjectRow
                        key={key}
                        row={row}
                        columns={columns}
                        absolute={absolute}
                        semantic={semantic}
                        drilldown={drilldown}
                        expanded={!!expanded?.has(key)}
                        onToggle={() => onToggle?.(key)}
                    />
                );
            })}
            {totalLabel && (
                <TotalRow
                    name={withIncomplete(totalLabel, sectionIncomplete(section.children))}
                    row={section}
                    columns={columns}
                    absolute={absolute}
                    semantic={semantic}
                    locale={totalLocale}
                />
            )}
        </React.Fragment>
    );
}

function ProjectRow({ row, columns, absolute, semantic = false, drilldown = true, expanded = false, onToggle }) {
    const categories = row.children || [];
    const hasCategories = drilldown && categories.length > 0;

    if (row.has_activity === false) {
        return (
            <tr>
                <td>
                    <div style={{ paddingInlineStart: 12 }}>
                        <span className="tw-text-charcoal-75">{row.name}</span>
                        <MissingRate fx={row.fx} />
                    </div>
                </td>
                <td colSpan={columns.length + 1} className="tw-text-center tw-text-charcoal-50">
                    {$t('Nothing budgeted in this period')}
                </td>
            </tr>
        );
    }

    return (
        <React.Fragment>
            <tr>
                <td className={classNames({ 'cursor-pointer': hasCategories })} onClick={hasCategories ? onToggle : undefined}>
                    <div className="tw-flex tw-items-center tw-gap-x-2" style={{ paddingInlineStart: 12 }}>
                        {hasCategories &&
                            (expanded ? (
                                <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal-50" />
                            ) : (
                                <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-charcoal-50" />
                            ))}
                        <span className="tw-text-charcoal-75">{row.name}</span>
                        <MissingRate fx={row.fx} />
                    </div>
                </td>
                {columns.map((column) => (
                    <Cell key={column.key} value={row.columns?.[column.key]?.total} absolute={absolute} semantic={semantic} locale={row.locale} />
                ))}
                <Cell value={row.total} absolute={absolute} semantic={semantic} locale={row.locale} />
            </tr>
            {hasCategories &&
                expanded &&
                categories.map((category, categoryIndex) => (
                    <CategoryRow
                        key={`category-${categoryIndex}`}
                        row={category}
                        columns={columns}
                        absolute={absolute}
                        semantic={semantic}
                        locale={row.locale}
                    />
                ))}
        </React.Fragment>
    );
}

/** A project left in its own currency says so; a converted one needs no mark here. */
function MissingRate({ fx }) {
    return fx?.missing ? <FxRate fx={fx} /> : null;
}

function CategoryRow({ row, columns, absolute, semantic = false, locale = null }) {
    return (
        <tr>
            <td>
                <div className="tw-text-charcoal-50" style={{ paddingInlineStart: 28 }}>
                    {row.name}
                </div>
            </td>
            {columns.map((column) => (
                <Cell key={column.key} value={row.columns?.[column.key]?.total} absolute={absolute} semantic={semantic} muted locale={locale} />
            ))}
            <Cell value={row.total} absolute={absolute} semantic={semantic} muted locale={locale} />
        </tr>
    );
}

function TotalRow({ name, row, columns, absolute = false, semantic = false, footer = false, locale = null }) {
    return (
        <tr>
            <td className="tw-font-medium">{name}</td>
            {columns.map((column) => (
                <Cell key={column.key} value={row.columns?.[column.key]?.total} absolute={absolute} semantic={semantic} footer={footer} locale={locale} bold />
            ))}
            <Cell value={row.total} absolute={absolute} semantic={semantic} footer={footer} locale={locale} bold />
        </tr>
    );
}

// On the filled footer a gain stays white and a loss is a light red that
// still reads on the blue.
function Cell({ value, absolute = false, semantic = false, footer = false, bold = false, muted = false, locale = null }) {
    const formatted = formatCell(value, absolute, locale);
    const isEmpty = isEmptyCell(formatted);

    let colour = semantic && !isEmpty ? (value >= 0 ? 'tw-text-green' : 'tw-text-red') : null;
    if (footer) {
        colour = semantic && !isEmpty && value < 0 ? 'tw-text-red-25' : null;
    }

    return (
        <td
            className={classNames('inbud-numeric-cell', colour, {
                'tw-font-medium': bold,
                'tw-text-charcoal-50 tw-italic': muted,
            })}
        >
            {formatted}
        </td>
    );
}
